import { FC, useState, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  useTheme,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material'
import {
  IconBrandFacebook,
  IconBrandTiktok,
  IconBrandX,
  IconCheck,
  IconCode,
  IconChevronDown,
  IconInfoCircle,
  IconClock,
  IconShieldCheck,
  IconBuildingStore,
  IconChartBar,
} from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import axiosInstance from '../../../../utils/axiosInstance'
import './style.scss'

interface Integration {
  id: string
  name: string
  description: string
  icon: React.ReactNode
  connected: boolean
  platforms: string[]
  color: string
  comingSoon?: boolean
  dbId?: string // Database ID for connected integrations
  backendData?: BackendIntegration // Full backend integration data
}

interface MetaPage {
  id: string
  name: string
  tasks: string[]
  category: string
  accessToken: string
}

interface BackendIntegration {
  id: string
  platform: string
  createdAt: string
  lastSyncedAt: string | null
  tokenExpiresAt?: string | null
  metaPageIds: string[]
  metaAdAccountIds: string[]
  platformData?: {
    pages?: MetaPage[]
    adAccounts?: any[]
    permissions?: string[]
  }
}

const Integrations: FC = () => {
  const intl = useIntl()
  const theme = useTheme()
  const location = useLocation()

  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: 'meta',
      name: 'Meta Business Suite',
      description:
        'Connect your Facebook and Instagram business accounts to import leads and campaigns',
      icon: <IconBrandFacebook size={40} />,
      connected: false,
      platforms: ['Facebook', 'Instagram'],
      color: '#1877F2',
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      description: 'Connect your TikTok business account to import leads and campaigns',
      icon: <IconBrandTiktok size={40} />,
      connected: false,
      platforms: ['TikTok'],
      color: '#000000',
      comingSoon: true,
    },
    {
      id: 'x',
      name: 'X (Twitter)',
      description: 'Connect your X business account to import leads and campaigns',
      icon: <IconBrandX size={40} />,
      connected: false,
      platforms: ['X'],
      color: '#000000',
      comingSoon: true,
    },
  ])

  const [loading, setLoading] = useState(true)
  const [alertMessage, setAlertMessage] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null)
  const [selectedPages, setSelectedPages] = useState<string[]>([])
  const [importing, setImporting] = useState(false)
  const [importResults, setImportResults] = useState<any>(null)
  const [showRawData, setShowRawData] = useState(false)
  const [showRawInsights, setShowRawInsights] = useState(false)
  const [rawInsights, setRawInsights] = useState<any>(null)
  const [loadingInsights, setLoadingInsights] = useState(false)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [selectedDetailsIntegration, setSelectedDetailsIntegration] = useState<Integration | null>(
    null
  )
  const [enablingPeriodicImport, setEnablingPeriodicImport] = useState(false)
  const [confirmPeriodicImportOpen, setConfirmPeriodicImportOpen] = useState(false)
  const [periodicImportResult, setPeriodicImportResult] = useState<{
    success: boolean
    message: string
    details?: any
  } | null>(null)
  const [manualImporting, setManualImporting] = useState(false)

  const fetchIntegrations = useCallback(async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get('/api/integrations')
      const backendIntegrations: BackendIntegration[] = response.data.integrations

      // Update integrations state with connected status
      setIntegrations((prev) =>
        prev.map((integration) => {
          const connected = backendIntegrations.find((bi) => bi.platform === integration.id)
          return {
            ...integration,
            connected: !!connected,
            dbId: connected?.id,
            backendData: connected,
          }
        })
      )
    } catch (error) {
      console.error('Error fetching integrations:', error)
      setAlertMessage({
        type: 'error',
        message: intl.formatMessage({
          id: 'integration-fetch-error',
          defaultMessage: 'Failed to load integrations',
        }),
      })
    } finally {
      setLoading(false)
    }
  }, [intl])

  useEffect(() => {
    // Check for success/error in URL params
    const params = new URLSearchParams(location.search)
    const success = params.get('success')
    const error = params.get('error')

    if (success) {
      setAlertMessage({
        type: 'success',
        message: intl.formatMessage({
          id: 'integration-connected-success',
          defaultMessage: 'Integration connected successfully!',
        }),
      })
      // Clean URL
      window.history.replaceState({}, '', '/user/integrations')
    } else if (error) {
      setAlertMessage({
        type: 'error',
        message: decodeURIComponent(error),
      })
      // Clean URL
      window.history.replaceState({}, '', '/user/integrations')
    }

    fetchIntegrations()
  }, [location.search, intl, fetchIntegrations])

  const handleConnect = (integrationId: string) => {
    if (integrationId === 'meta') {
      handleMetaConnect()
    }
  }

  const handleMetaConnect = () => {
    const clientId = process.env.REACT_APP_META_RECRUSPINCONNECT_APP_ID
    const redirectUri = `${process.env.REACT_APP_SERVER_URL}/user/integrations/callback`
    // Required scopes:
    // - leads_retrieval: Access lead forms and lead data
    // - pages_manage_ads: REQUIRED prerequisite for leads_retrieval
    // - pages_show_list: List pages the user manages
    // - ads_read: Read ad account data
    // - business_management: Access Business Manager owned pages (if applicable)
    const scope = 'leads_retrieval,pages_manage_ads,pages_show_list,ads_read,business_management'
    const state = encodeURIComponent(window.location.origin) // To redirect back to frontend after callback

    const authUrl = `https://www.facebook.com/v22.0/dialog/oauth?client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&scope=${encodeURIComponent(scope)}&response_type=code&state=${state}`

    window.location.href = authUrl
  }

  const handleDisconnect = async (integration: Integration) => {
    if (!integration.dbId) return

    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        intl.formatMessage({
          id: 'integration-disconnect-confirm',
          defaultMessage: 'Are you sure you want to disconnect this integration?',
        })
      )
    ) {
      return
    }

    try {
      await axiosInstance.delete(`/api/integrations/${integration.dbId}`)
      setAlertMessage({
        type: 'success',
        message: intl.formatMessage({
          id: 'integration-disconnected-success',
          defaultMessage: 'Integration disconnected successfully',
        }),
      })
      // Refresh integrations
      fetchIntegrations()
    } catch (error) {
      console.error('Error disconnecting integration:', error)
      setAlertMessage({
        type: 'error',
        message: intl.formatMessage({
          id: 'integration-disconnect-error',
          defaultMessage: 'Failed to disconnect integration',
        }),
      })
    }
  }

  const handleImportLeads = (integration: Integration) => {
    setSelectedIntegration(integration)
    setSelectedPages([])
    setImportResults(null)
    setImportModalOpen(true)
  }

  const handleCloseImportModal = () => {
    setImportModalOpen(false)
    setSelectedIntegration(null)
    setSelectedPages([])
    setImportResults(null)
    setShowRawData(false)
    setShowRawInsights(false)
    setRawInsights(null)
  }

  const handleViewRawInsights = async () => {
    if (!selectedIntegration?.dbId) return
    if (rawInsights) {
      setShowRawInsights((prev) => !prev)
      return
    }
    setLoadingInsights(true)
    try {
      const response = await axiosInstance.get(
        `/api/integrations/${selectedIntegration.dbId}/ad-insights`
      )
      setRawInsights(response.data)
      setShowRawInsights(true)
    } catch (err) {
      console.error('Error fetching ad insights:', err)
    } finally {
      setLoadingInsights(false)
    }
  }

  const handlePageSelection = (pageId: string) => {
    setSelectedPages((prev) =>
      prev.includes(pageId) ? prev.filter((id) => id !== pageId) : [...prev, pageId]
    )
  }

  const handleImport = async () => {
    if (!selectedIntegration?.dbId || selectedPages.length === 0) return

    setImporting(true)
    setImportResults(null)

    try {
      const response = await axiosInstance.post('/api/integrations/import-leads', {
        integrationId: selectedIntegration.dbId,
        pageIds: selectedPages,
      })

      setImportResults(response.data)
      setAlertMessage({
        type: 'success',
        message: intl.formatMessage({
          id: 'leads-imported-success',
          defaultMessage: 'Leads imported successfully',
        }),
      })
    } catch (error: any) {
      console.error('Error importing leads:', error)
      setAlertMessage({
        type: 'error',
        message:
          error.response?.data?.error ||
          intl.formatMessage({
            id: 'leads-import-error',
            defaultMessage: 'Failed to import leads',
          }),
      })
    } finally {
      setImporting(false)
    }
  }

  const getPages = (): MetaPage[] => {
    return selectedIntegration?.backendData?.platformData?.pages || []
  }

  const handleViewDetails = (integration: Integration) => {
    setSelectedDetailsIntegration(integration)
    setDetailsModalOpen(true)
  }

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false)
    setSelectedDetailsIntegration(null)
  }

  const formatTokenExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return 'N/A'
    const expiryDate = new Date(expiresAt)
    const now = new Date()
    const diffMs = expiryDate.getTime() - now.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays < 0) {
      return `Expired ${Math.abs(diffDays)} days ago`
    }
    if (diffDays === 0) {
      return 'Expires today'
    }
    if (diffDays === 1) {
      return 'Expires tomorrow'
    }
    if (diffDays < 30) {
      return `Expires in ${diffDays} days`
    }
    return `Expires on ${expiryDate.toLocaleDateString()}`
  }

  const handleEnablePeriodicImport = () => {
    if (!selectedIntegration?.dbId || selectedPages.length === 0) return
    setConfirmPeriodicImportOpen(true)
  }

  const handleConfirmPeriodicImport = async () => {
    if (!selectedIntegration?.dbId || selectedPages.length === 0) return

    setConfirmPeriodicImportOpen(false)
    setEnablingPeriodicImport(true)
    setPeriodicImportResult(null)

    try {
      const response = await axiosInstance.post('/api/integrations/enable-periodic-import', {
        integrationId: selectedIntegration.dbId,
        pageIds: selectedPages,
      })

      const pages = getPages()
      const selectedPageNames = pages.filter((p) => selectedPages.includes(p.id)).map((p) => p.name)

      setPeriodicImportResult({
        success: true,
        message: intl.formatMessage({
          id: 'periodic-import-enabled',
          defaultMessage: 'Periodic import enabled successfully!',
        }),
        details: {
          pages: selectedPageNames,
          pageCount: selectedPages.length,
          interval: 'hourly',
          integrationId: response.data.integrationId,
          totalForms: response.data.currentTotals?.forms || 0,
          totalLeads: response.data.currentTotals?.leads || 0,
        },
      })

      // Refresh integrations
      fetchIntegrations()
    } catch (error: any) {
      console.error('Error enabling periodic import:', error)
      setPeriodicImportResult({
        success: false,
        message:
          error.response?.data?.error ||
          intl.formatMessage({
            id: 'periodic-import-enable-error',
            defaultMessage: 'Failed to enable periodic import',
          }),
      })
    } finally {
      setEnablingPeriodicImport(false)
    }
  }

  const handleClosePeriodicImportResult = () => {
    setPeriodicImportResult(null)
    if (periodicImportResult?.success) {
      handleCloseImportModal()
    }
  }

  const handleManualImportNow = async () => {
    if (!selectedIntegration?.dbId || selectedPages.length === 0) return

    setManualImporting(true)

    try {
      const response = await axiosInstance.post('/api/integrations/manual-import', {
        integrationId: selectedIntegration.dbId,
        pageIds: selectedPages,
      })

      const pages = getPages()
      const selectedPageNames = pages.filter((p) => selectedPages.includes(p.id)).map((p) => p.name)

      setPeriodicImportResult({
        success: true,
        message: intl.formatMessage({
          id: 'manual-import-success',
          defaultMessage: 'Manual import completed successfully!',
        }),
        details: {
          pages: selectedPageNames,
          pageCount: selectedPages.length,
          totalForms: response.data.totalForms || 0,
          totalLeads: response.data.totalLeads || 0,
          pageResults: response.data.pageResults || {},
          isManual: true,
        },
      })

      // Refresh integrations
      fetchIntegrations()
    } catch (error: any) {
      console.error('Error during manual import:', error)
      setPeriodicImportResult({
        success: false,
        message:
          error.response?.data?.error ||
          intl.formatMessage({
            id: 'manual-import-error',
            defaultMessage: 'Failed to complete manual import',
          }),
      })
    } finally {
      setManualImporting(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <div className="Integrations">
      <Container className="integrations-container">
        {alertMessage && (
          <Alert severity={alertMessage.type} onClose={() => setAlertMessage(null)} sx={{ mb: 3 }}>
            {alertMessage.message}
          </Alert>
        )}

        <Box mb={3}>
          <Typography variant="h4" gutterBottom>
            <FormattedMessage id="integrations" defaultMessage="Integrations" />
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage
              id="integrations-description"
              defaultMessage="Connect your social media accounts to import leads and campaigns automatically"
            />
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {integrations.map((integration) => (
            <Grid item xs={12} md={6} lg={4} key={integration.id}>
              <Card
                className="integration-card"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: integration.connected
                    ? `2px solid ${theme.palette.success.main}`
                    : `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        color: integration.color,
                        mr: 2,
                      }}
                    >
                      {integration.icon}
                    </Box>
                    <Box flexGrow={1}>
                      <Typography variant="h5" component="div">
                        {integration.name}
                      </Typography>
                      {integration.connected && (
                        <Chip
                          icon={<IconCheck size={16} />}
                          label={intl.formatMessage({
                            id: 'connected',
                            defaultMessage: 'Connected',
                          })}
                          color="success"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                      {integration.comingSoon && (
                        <Chip
                          label={intl.formatMessage({
                            id: 'coming-soon',
                            defaultMessage: 'Coming Soon',
                          })}
                          color="default"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Typography variant="body2" color="textSecondary" mb={2}>
                    {integration.description}
                  </Typography>

                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    {integration.platforms.map((platform) => (
                      <Box key={platform} display="flex" alignItems="center" gap={0.5}>
                        {platform === 'Facebook' && (
                          <img src="/socials/fb.svg" alt="Facebook" width="20px" height="20px" />
                        )}
                        {platform === 'Instagram' && (
                          <img src="/socials/ig.svg" alt="Instagram" width="20px" height="20px" />
                        )}
                        {platform === 'TikTok' && (
                          <img src="/socials/tiktok.svg" alt="TikTok" width="20px" height="20px" />
                        )}
                        {platform === 'X' && <IconBrandX size={20} />}
                        <Typography variant="caption">{platform}</Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>

                <Box p={2} pt={0}>
                  {integration.comingSoon ? (
                    <Button fullWidth variant="outlined" disabled>
                      <FormattedMessage id="coming-soon" defaultMessage="Coming Soon" />
                    </Button>
                  ) : integration.connected ? (
                    <Box display="flex" gap={1} flexDirection="column">
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleImportLeads(integration)}
                      >
                        <FormattedMessage id="import-leads" defaultMessage="Import Leads" />
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleViewDetails(integration)}
                      >
                        <FormattedMessage id="view-details" defaultMessage="View Details" />
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        onClick={() => handleDisconnect(integration)}
                      >
                        <FormattedMessage id="disconnect" defaultMessage="Disconnect" />
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleConnect(integration.id)}
                    >
                      <FormattedMessage id="connect" defaultMessage="Connect" />
                    </Button>
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Import Leads Modal */}
        <Dialog open={importModalOpen} onClose={handleCloseImportModal} maxWidth="md" fullWidth>
          <DialogTitle>
            <FormattedMessage id="import-leads-modal-title" defaultMessage="Import Leads" />
          </DialogTitle>
          <DialogContent>
            {!importResults ? (
              <>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  <FormattedMessage
                    id="import-leads-modal-description"
                    defaultMessage="Select the pages you want to import leads from. We'll fetch the first lead from each selected page."
                  />
                </Typography>

                <FormGroup>
                  {getPages().map((page) => {
                    return (
                      <Box key={page.id} display="flex" alignItems="center" mb={1}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPages.includes(page.id)}
                              onChange={() => handlePageSelection(page.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1">{page.name}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {page.category} • ID: {page.id}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    )
                  })}
                </FormGroup>

                {getPages().length === 0 && (
                  <Alert severity="info">
                    <FormattedMessage
                      id="no-pages-found"
                      defaultMessage="No pages found in this integration"
                    />
                  </Alert>
                )}
              </>
            ) : (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    <FormattedMessage id="import-results" defaultMessage="Import Results" />
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<IconCode size={18} />}
                      onClick={() => setShowRawData(!showRawData)}
                    >
                      {showRawData ? (
                        <FormattedMessage id="hide-raw-data" defaultMessage="Hide Raw Data" />
                      ) : (
                        <FormattedMessage id="show-raw-data" defaultMessage="Show Raw Data" />
                      )}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        loadingInsights ? (
                          <CircularProgress size={14} />
                        ) : (
                          <IconChartBar size={18} />
                        )
                      }
                      onClick={handleViewRawInsights}
                      disabled={loadingInsights}
                    >
                      {showRawInsights ? (
                        <FormattedMessage
                          id="hide-raw-insights"
                          defaultMessage="Hide Raw Insights"
                        />
                      ) : (
                        <FormattedMessage
                          id="view-raw-insights"
                          defaultMessage="View Raw Insights"
                        />
                      )}
                    </Button>
                  </Box>
                </Box>

                {showRawInsights && rawInsights && (
                  <Box
                    sx={{
                      backgroundColor: '#f5f5f5',
                      padding: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '400px',
                      mb: 2,
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(rawInsights, null, 2)}
                    </pre>
                  </Box>
                )}

                {showRawData ? (
                  <Box
                    sx={{
                      backgroundColor: '#f5f5f5',
                      padding: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '400px',
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(importResults, null, 2)}
                    </pre>
                  </Box>
                ) : (
                  <Box>
                    {importResults?.message && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        {importResults.message}
                      </Alert>
                    )}

                    {/* Iterate through pages */}
                    {importResults?.results &&
                      Object.entries(importResults.results).map(
                        ([pageId, pageResult]: [string, any]) => (
                          <Box key={pageId} mb={3}>
                            {/* Page Header */}
                            <Box
                              sx={{
                                backgroundColor: theme.palette.grey[100],
                                padding: 2,
                                borderRadius: 1,
                                mb: 1,
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Box>
                                  <Typography variant="h6">{pageResult.pageName}</Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    Page ID: {pageId}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={`${pageResult.totalForms || 0} Forms`}
                                  color="primary"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>

                            {/* Error Message */}
                            {pageResult.error && (
                              <Alert severity="error" sx={{ mb: 2 }}>
                                {pageResult.error}
                              </Alert>
                            )}

                            {/* Forms List */}
                            {pageResult.forms && pageResult.forms.length > 0 ? (
                              pageResult.forms.map((form: any) => (
                                <Accordion key={form.id} sx={{ mb: 1 }}>
                                  <AccordionSummary expandIcon={<IconChevronDown />}>
                                    <Box display="flex" alignItems="center" gap={2} width="100%">
                                      <Box flexGrow={1}>
                                        <Typography variant="body1" fontWeight={500}>
                                          {form.name}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                          Form ID: {form.id}
                                        </Typography>
                                      </Box>
                                      <Box display="flex" gap={1} alignItems="center">
                                        <Chip
                                          label={form.status}
                                          color={form.status === 'ACTIVE' ? 'success' : 'default'}
                                          size="small"
                                        />
                                        <Chip
                                          label={`${form.leadsCount} leads`}
                                          color="primary"
                                          variant="outlined"
                                          size="small"
                                        />
                                      </Box>
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Box>
                                      <Typography variant="subtitle2" gutterBottom>
                                        <FormattedMessage
                                          id="form-info"
                                          defaultMessage="Form Information"
                                        />
                                      </Typography>
                                      <Box display="flex" gap={2} mb={2}>
                                        <Typography variant="body2" color="textSecondary">
                                          Created: {new Date(form.createdTime).toLocaleDateString()}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                          Total Leads: {form.leadsCount}
                                        </Typography>
                                      </Box>

                                      <Divider sx={{ my: 2 }} />

                                      {/* Leads */}
                                      {form.leads && form.leads.length > 0 ? (
                                        <>
                                          <Typography variant="subtitle2" gutterBottom>
                                            <FormattedMessage
                                              id="first-lead"
                                              defaultMessage="First Lead (Sample)"
                                            />
                                          </Typography>
                                          {form.leads.map((lead: any, idx: number) => (
                                            <Box
                                              key={lead.id}
                                              sx={{
                                                backgroundColor: theme.palette.grey[50],
                                                padding: 2,
                                                borderRadius: 1,
                                                mb: idx < form.leads.length - 1 ? 1 : 0,
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                fontWeight={500}
                                                gutterBottom
                                              >
                                                Lead ID: {lead.id}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                display="block"
                                              >
                                                Created:{' '}
                                                {new Date(lead.created_time).toLocaleString()}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                display="block"
                                              >
                                                Platform: {lead.platform?.toUpperCase() || 'N/A'}
                                              </Typography>
                                              {lead.campaign_name && (
                                                <Typography
                                                  variant="caption"
                                                  color="textSecondary"
                                                  display="block"
                                                >
                                                  Campaign: {lead.campaign_name}
                                                </Typography>
                                              )}
                                              {lead.ad_name && (
                                                <Typography
                                                  variant="caption"
                                                  color="textSecondary"
                                                  display="block"
                                                >
                                                  Ad: {lead.ad_name}
                                                </Typography>
                                              )}
                                            </Box>
                                          ))}
                                        </>
                                      ) : (
                                        <Alert severity="info">
                                          <FormattedMessage
                                            id="no-leads-in-form"
                                            defaultMessage="No leads found in this form"
                                          />
                                        </Alert>
                                      )}
                                    </Box>
                                  </AccordionDetails>
                                </Accordion>
                              ))
                            ) : (
                              <Alert severity="info">
                                <FormattedMessage
                                  id="no-forms-found"
                                  defaultMessage="No forms found for this page"
                                />
                              </Alert>
                            )}
                          </Box>
                        )
                      )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseImportModal} color="inherit">
              <FormattedMessage id="close" defaultMessage="Close" />
            </Button>
            {!importResults && (
              <Button
                onClick={handleImport}
                variant="contained"
                disabled={selectedPages.length === 0 || importing}
              >
                {importing ? (
                  <CircularProgress size={20} />
                ) : (
                  <FormattedMessage id="import" defaultMessage="Import" />
                )}
              </Button>
            )}
            {importResults && (
              <>
                <Button
                  onClick={handleManualImportNow}
                  variant="outlined"
                  color="primary"
                  disabled={manualImporting || enablingPeriodicImport}
                >
                  {manualImporting ? (
                    <CircularProgress size={20} />
                  ) : (
                    <FormattedMessage
                      id="import-manually-now"
                      defaultMessage="Import Manually Now"
                    />
                  )}
                </Button>
                <Button
                  onClick={handleEnablePeriodicImport}
                  variant="contained"
                  color="primary"
                  disabled
                >
                  <FormattedMessage
                    id="start-periodic-imports-beta"
                    defaultMessage="Start Periodic Imports (Coming Soon)"
                  />
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Integration Details Modal */}
        <Dialog open={detailsModalOpen} onClose={handleCloseDetailsModal} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ color: selectedDetailsIntegration?.color }}>
                {selectedDetailsIntegration?.icon}
              </Box>
              <Box>
                <Typography variant="h5">{selectedDetailsIntegration?.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  <FormattedMessage id="integration-details" defaultMessage="Integration Details" />
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedDetailsIntegration?.backendData && (
              <Box>
                {/* General Info */}
                <Box mb={3}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <IconInfoCircle size={20} />
                    <Typography variant="h6">
                      <FormattedMessage
                        id="general-information"
                        defaultMessage="General Information"
                      />
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        <FormattedMessage id="platform" defaultMessage="Platform" />
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedDetailsIntegration.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        <FormattedMessage id="connected-on" defaultMessage="Connected On" />
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {new Date(
                          selectedDetailsIntegration.backendData.createdAt
                        ).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        <FormattedMessage id="last-synced" defaultMessage="Last Synced" />
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedDetailsIntegration.backendData.lastSyncedAt
                          ? new Date(
                              selectedDetailsIntegration.backendData.lastSyncedAt
                            ).toLocaleString()
                          : 'Never'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconClock size={16} />
                        <Typography variant="body2" color="textSecondary">
                          <FormattedMessage id="token-expiry" defaultMessage="Token Expiry" />
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight={500}>
                        {formatTokenExpiry(
                          selectedDetailsIntegration.backendData.tokenExpiresAt || null
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Meta-specific data */}
                {selectedDetailsIntegration.id === 'meta' &&
                  selectedDetailsIntegration.backendData.platformData && (
                    <>
                      {/* Pages */}
                      {selectedDetailsIntegration.backendData.platformData.pages &&
                        selectedDetailsIntegration.backendData.platformData.pages.length > 0 && (
                          <Box mb={3}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                              <IconBuildingStore size={20} />
                              <Typography variant="h6">
                                <FormattedMessage
                                  id="connected-pages"
                                  defaultMessage="Connected Pages"
                                />
                              </Typography>
                              <Chip
                                label={
                                  selectedDetailsIntegration.backendData.platformData.pages.length
                                }
                                size="small"
                                color="primary"
                              />
                            </Box>
                            <Box display="flex" flexDirection="column" gap={2}>
                              {selectedDetailsIntegration.backendData.platformData.pages.map(
                                (page: MetaPage) => (
                                  <Card key={page.id} variant="outlined">
                                    <CardContent>
                                      <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="start"
                                      >
                                        <Box>
                                          <Typography variant="body1" fontWeight={500}>
                                            {page.name}
                                          </Typography>
                                          <Typography variant="caption" color="textSecondary">
                                            {page.category} • ID: {page.id}
                                          </Typography>
                                          <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                                            {page.tasks.map((task) => (
                                              <Chip
                                                key={task}
                                                label={task}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem' }}
                                              />
                                            ))}
                                          </Box>
                                        </Box>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                )
                              )}
                            </Box>
                          </Box>
                        )}

                      <Divider sx={{ my: 3 }} />

                      {/* Ad Accounts */}
                      {selectedDetailsIntegration.backendData.platformData.adAccounts &&
                        selectedDetailsIntegration.backendData.platformData.adAccounts.length >
                          0 && (
                          <Box mb={3}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                              <Typography variant="h6">
                                <FormattedMessage id="ad-accounts" defaultMessage="Ad Accounts" />
                              </Typography>
                              <Chip
                                label={
                                  selectedDetailsIntegration.backendData.platformData.adAccounts
                                    .length
                                }
                                size="small"
                                color="primary"
                              />
                            </Box>
                            <Box display="flex" flexDirection="column" gap={2}>
                              {selectedDetailsIntegration.backendData.platformData.adAccounts.map(
                                (account: any) => (
                                  <Card key={account.id} variant="outlined">
                                    <CardContent>
                                      <Typography variant="body1" fontWeight={500}>
                                        {account.name}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        ID: {account.id}
                                      </Typography>
                                      <Box display="flex" gap={1} mt={1}>
                                        <Chip
                                          label={`${account.currency}`}
                                          size="small"
                                          variant="outlined"
                                        />
                                        <Chip
                                          label={account.timezone}
                                          size="small"
                                          variant="outlined"
                                        />
                                        <Chip
                                          label={
                                            account.accountStatus === 1 ? 'Active' : 'Inactive'
                                          }
                                          size="small"
                                          color={
                                            account.accountStatus === 1 ? 'success' : 'default'
                                          }
                                        />
                                      </Box>
                                    </CardContent>
                                  </Card>
                                )
                              )}
                            </Box>
                          </Box>
                        )}

                      <Divider sx={{ my: 3 }} />

                      {/* Permissions */}
                      {selectedDetailsIntegration.backendData.platformData.permissions &&
                        selectedDetailsIntegration.backendData.platformData.permissions.length >
                          0 && (
                          <Box mb={2}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                              <IconShieldCheck size={20} />
                              <Typography variant="h6">
                                <FormattedMessage id="permissions" defaultMessage="Permissions" />
                              </Typography>
                              <Chip
                                label={
                                  selectedDetailsIntegration.backendData.platformData.permissions
                                    .length
                                }
                                size="small"
                                color="primary"
                              />
                            </Box>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              {selectedDetailsIntegration.backendData.platformData.permissions.map(
                                (permission: string) => (
                                  <Chip
                                    key={permission}
                                    label={permission}
                                    color="success"
                                    variant="outlined"
                                    icon={<IconCheck size={16} />}
                                  />
                                )
                              )}
                            </Box>
                          </Box>
                        )}
                    </>
                  )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailsModal} color="inherit">
              <FormattedMessage id="close" defaultMessage="Close" />
            </Button>
          </DialogActions>
        </Dialog>

        {/* Periodic Import Confirmation Dialog */}
        <Dialog
          open={confirmPeriodicImportOpen}
          onClose={() => setConfirmPeriodicImportOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <IconClock size={24} color={theme.palette.primary.main} />
              <Typography variant="h6">
                <FormattedMessage
                  id="enable-periodic-imports"
                  defaultMessage="Enable Periodic Imports"
                />
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              <FormattedMessage
                id="periodic-import-info"
                defaultMessage="This will automatically import leads from the selected pages every hour."
              />
            </Alert>
            <Typography variant="body2" paragraph>
              <FormattedMessage
                id="periodic-import-description"
                defaultMessage="Once enabled, the system will:"
              />
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="periodic-import-item-1"
                    defaultMessage="Check for new leads every hour"
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="periodic-import-item-2"
                    defaultMessage="Automatically import new forms and leads"
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="periodic-import-item-3"
                    defaultMessage="Apply all configured hooks (CV requests, notifications, etc.)"
                  />
                </Typography>
              </li>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              <FormattedMessage id="selected-pages" defaultMessage="Selected Pages:" />
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {getPages()
                .filter((p) => selectedPages.includes(p.id))
                .map((page) => (
                  <Chip key={page.id} label={page.name} size="small" color="primary" />
                ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmPeriodicImportOpen(false)} color="inherit">
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
            <Button onClick={handleConfirmPeriodicImport} variant="contained" color="primary">
              <FormattedMessage id="enable" defaultMessage="Enable" />
            </Button>
          </DialogActions>
        </Dialog>

        {/* Periodic Import Result Dialog */}
        <Dialog
          open={enablingPeriodicImport || periodicImportResult !== null}
          onClose={enablingPeriodicImport ? undefined : handleClosePeriodicImportResult}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {enablingPeriodicImport ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={24} />
                <Typography variant="h6">
                  <FormattedMessage
                    id="enabling-periodic-import"
                    defaultMessage="Enabling Periodic Import..."
                  />
                </Typography>
              </Box>
            ) : periodicImportResult?.success ? (
              <Box display="flex" alignItems="center" gap={1}>
                <IconCheck size={24} color={theme.palette.success.main} />
                <Typography variant="h6" color="success.main">
                  <FormattedMessage id="success" defaultMessage="Success!" />
                </Typography>
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <IconInfoCircle size={24} color={theme.palette.error.main} />
                <Typography variant="h6" color="error.main">
                  <FormattedMessage id="error" defaultMessage="Error" />
                </Typography>
              </Box>
            )}
          </DialogTitle>
          <DialogContent>
            {enablingPeriodicImport ? (
              <Box display="flex" flexDirection="column" alignItems="center" py={3}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="body1" color="textSecondary" align="center">
                  <FormattedMessage
                    id="setting-up-periodic-import"
                    defaultMessage="Setting up periodic import and starting initial sync..."
                  />
                </Typography>
              </Box>
            ) : periodicImportResult ? (
              <Box>
                <Alert severity={periodicImportResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                  {periodicImportResult.message}
                </Alert>

                {periodicImportResult.success && periodicImportResult.details && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      <FormattedMessage id="import-details" defaultMessage="Import Details:" />
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: theme.palette.grey[50],
                        padding: 2,
                        borderRadius: 1,
                        mt: 1,
                      }}
                    >
                      <Grid container spacing={2}>
                        {!periodicImportResult.details.isManual && (
                          <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary">
                              <FormattedMessage id="interval" defaultMessage="Interval" />
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              <FormattedMessage id="hourly" defaultMessage="Hourly" />
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            <FormattedMessage id="pages-count" defaultMessage="Pages" />
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {periodicImportResult.details.pageCount}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            <FormattedMessage
                              id="new-leadforms-imported"
                              defaultMessage="New Leadforms Imported"
                            />
                          </Typography>
                          <Typography variant="body2" fontWeight={500} color="success.main">
                            +{periodicImportResult.details.totalForms}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            <FormattedMessage
                              id="new-leads-imported"
                              defaultMessage="New Leads Imported"
                            />
                          </Typography>
                          <Typography variant="body2" fontWeight={500} color="success.main">
                            +{periodicImportResult.details.totalLeads}
                          </Typography>
                        </Grid>
                      </Grid>

                      {periodicImportResult.details.pageResults && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                            mb={1}
                          >
                            <FormattedMessage
                              id="per-page-breakdown"
                              defaultMessage="Per-Page Breakdown:"
                            />
                          </Typography>
                          <Box display="flex" flexDirection="column" gap={1}>
                            {Object.entries(periodicImportResult.details.pageResults).map(
                              ([pageId, result]: [string, any]) => (
                                <Box
                                  key={pageId}
                                  sx={{
                                    backgroundColor: theme.palette.background.paper,
                                    padding: 1.5,
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={500}>
                                    {result.pageName}
                                  </Typography>
                                  {result.error ? (
                                    <Typography variant="caption" color="error">
                                      Error: {result.error}
                                    </Typography>
                                  ) : (
                                    <Box display="flex" gap={2} mt={0.5}>
                                      <Typography variant="caption" color="textSecondary">
                                        +{result.newForms} forms
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        +{result.newLeads} leads
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        (Total: {result.totalForms} forms, {result.totalLeads}{' '}
                                        leads)
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )
                            )}
                          </Box>
                        </>
                      )}

                      {!periodicImportResult.details.isManual && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                            mb={1}
                          >
                            <FormattedMessage
                              id="monitored-pages"
                              defaultMessage="Monitored Pages:"
                            />
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {periodicImportResult.details.pages.map((pageName: string) => (
                              <Chip
                                key={pageName}
                                label={pageName}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </>
                      )}
                    </Box>

                    {periodicImportResult.details.isManual ? (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        <FormattedMessage
                          id="manual-import-completed"
                          defaultMessage="Manual import completed. All new forms and leads have been imported into the database."
                        />
                      </Alert>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <FormattedMessage
                          id="initial-import-started"
                          defaultMessage="Initial import has started in the background. New leads will be imported automatically every hour."
                        />
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            ) : null}
          </DialogContent>
          {!enablingPeriodicImport && (
            <DialogActions>
              <Button onClick={handleClosePeriodicImportResult} variant="contained">
                <FormattedMessage id="close" defaultMessage="Close" />
              </Button>
            </DialogActions>
          )}
        </Dialog>
      </Container>
    </div>
  )
}

export default Integrations
